import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Video, Clock, Loader2, Radio } from 'lucide-react';
import { formatTimeLocal, formatLocalWeekdayLong, getTimezoneAbbr } from '../lib/utils';

export default function PublicHost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = () => {
    api.get(`/api/public/host/${slug}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Invalid link'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [slug]);

  // Poll every 15s. V14.5 — must keep refreshing even after one session
  // becomes startable, because parallel sessions may flip status / new
  // sessions may enter the buffer.
  useEffect(() => {
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">{error}</p></div>;

  const sessions = data?.sessions || (data?.session ? [data.session] : []);
  const hasMultiple = sessions.length >= 2;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="rounded-2xl w-full max-w-md">
        <CardContent className="pt-6 space-y-5">
          <div className="text-center">
            <img src="/logo.png" alt="Soul Sanskar" className="h-12 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-semibold">{data?.batch_name}</h1>
            <p className="text-sm text-muted-foreground mt-1">Instructor Portal</p>
          </div>

          {sessions.length === 0 ? (
            <p className="text-center text-muted-foreground py-4" data-testid="public-host-no-session">
              {data?.message || 'No sessions scheduled'}
            </p>
          ) : hasMultiple ? (
            <div className="space-y-3" data-testid="public-host-parallel">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                </span>
                <p className="text-sm font-semibold text-emerald-700">{data?.message}</p>
              </div>
              <p className="text-xs text-muted-foreground text-center mb-3">
                Pick the class you're hosting — multiple are happening in this batch.
              </p>
              {sessions.map(s => (
                <ParallelHostRow key={s.id} session={s} />
              ))}
              <p className="text-[10px] text-muted-foreground text-center mt-2">Auto-refreshes every 15s</p>
            </div>
          ) : (
            <SingleHostCard session={sessions[0]} message={data?.message} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function SingleHostCard({ session, message }) {
  const canStart = session?.can_start;
  const hostUrl = session?.zoom_host_web_url || session?.zoom_host_url;
  const isLive = session?.start_state === 'live';
  const isSoon = session?.start_state === 'starting_soon';
  const minsLeft = Math.max(0, Math.round(session?.minutes_until_start || 0));

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-muted/30 text-center">
        <p className="text-sm text-muted-foreground">
          {/* V14.5 Part 11 — Render in the host's LOCAL timezone (was IST
              date string treated as local). */}
          {formatLocalWeekdayLong(session.scheduled_date, session.scheduled_time)}
        </p>
        <p className="text-2xl font-semibold mt-1">{formatTimeLocal(session.scheduled_time, session.scheduled_date)}</p>
        <p className="text-xs text-muted-foreground">{getTimezoneAbbr()}</p>
        <p className="text-sm text-muted-foreground">{session.duration_minutes} minutes</p>
        {isLive ? (
          <Badge className="mt-2 bg-rose-600 text-white border-0 gap-1">
            <Radio className="h-3 w-3" />Live Now
          </Badge>
        ) : isSoon ? (
          <Badge variant="outline" className="mt-2 bg-amber-50 border-amber-200 text-amber-800">
            <Clock className="h-3 w-3 mr-1" />Starts in {minsLeft}m
          </Badge>
        ) : (
          <Badge variant="outline" className="mt-2">{session.status}</Badge>
        )}
      </div>

      {canStart && hostUrl ? (
        <div className="space-y-2">
          <Button
            className="w-full h-12 text-lg"
            onClick={() => window.open(hostUrl, '_blank')}
            data-testid="public-host-start-btn"
          >
            <Video className="h-5 w-5 mr-2" />{isLive ? 'Rejoin as Host' : 'Start Class as Host'}
          </Button>
          <p className="text-[11px] text-center text-muted-foreground">
            Opens in your browser as host — skips the waiting room.
            {session.zoom_host_url && (
              <> <button className="underline" onClick={() => window.open(session.zoom_host_url, '_blank')}>Open in Zoom app</button> instead.</>
            )}
          </p>
        </div>
      ) : (
        <div className="text-center space-y-2">
          <Button className="w-full" disabled data-testid="public-host-waiting-btn">
            <Clock className="h-4 w-4 mr-2" />Waiting for start window…
          </Button>
          <p className="text-xs text-muted-foreground">{message}</p>
          <p className="text-xs text-muted-foreground">This page auto-refreshes every 15 seconds</p>
        </div>
      )}
    </div>
  );
}


function ParallelHostRow({ session }) {
  const canStart = session?.can_start;
  const hostUrl = session?.zoom_host_web_url || session?.zoom_host_url;
  const isLive = session?.start_state === 'live';
  const isSoon = session?.start_state === 'starting_soon';
  const minsLeft = Math.max(0, Math.round(session?.minutes_until_start || 0));

  return (
    <Card className={`rounded-xl border-2 transition-colors ${canStart ? 'hover:border-primary/50' : 'opacity-70'}`} data-testid={`public-host-row-${session.id}`}>
      <CardContent className="pt-3 pb-3 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{session.title || 'Session'}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {isLive ? (
              <Badge className="bg-rose-600 text-white border-0 text-[9px]"><Radio className="h-2.5 w-2.5 mr-1" />Live</Badge>
            ) : isSoon ? (
              <Badge variant="outline" className="text-[9px] bg-amber-50 border-amber-200 text-amber-800"><Clock className="h-2.5 w-2.5 mr-1" />Starts in {minsLeft}m</Badge>
            ) : (
              <Badge variant="outline" className="text-[9px]">{session.status}</Badge>
            )}
            <span className="text-xs text-muted-foreground">
              {formatTimeLocal(session.scheduled_time, session.scheduled_date)} · {session.duration_minutes}m
            </span>
          </div>
        </div>
        {canStart && hostUrl ? (
          <Button
            size="sm"
            onClick={() => window.open(hostUrl, '_blank')}
            data-testid={`public-host-start-${session.id}`}
          >
            <Video className="h-3.5 w-3.5 mr-1" />{isLive ? 'Rejoin' : 'Start'}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled
            className="opacity-50"
            data-testid={`public-host-pending-${session.id}`}
          >
            <Clock className="h-3.5 w-3.5 mr-1" />Waiting
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
